<template>
  <div>
    <CustomCard
      :header="`CREDIT MEMOS`"
      subHeader="List of all credit memos.">
      <template v-slot:body>
        <Full
          @create="setCreate()"
          :noCreateButton="!isAccessible($options.CreditMemoPermissions.ADD.id)"
          placeholder="Search Here..."
          :isServerSide="true"
          @search="loadCreditMemos()"
          @paginate="loadCreditMemos()"
          :filter="filters.creditMemo"
          :table="tables.creditMemos"
          :pagination="paginations.creditMemo"
          buttonLabel="Create New Credit Memo"
          :sortChanged="sortChanged">
          <template #other-filter>
            <div class="credit-memo__filter">
              <v-select
                class="billing-period__select"
                :options="options.billingPeriods.items"
                :disabled="options.billingPeriods.isBusy"
                :loading="options.billingPeriods.isBusy"
                label="month"
                :filterBy="filterBillingPeriod"
                v-model="selectedBillingPeriod"
                @input="onChangeBillingPeriod"
              >
                <template #selected-option="{ month, year }">
                  {{ month.name + ' ' + year }}
                </template>
                <template #option="{ month, year }">
                  {{ (month && month.name || '' ) + ' ' + year }}
                </template>
                <template #spinner="{ loading }">
                  <div v-if="loading" style="border-left-color: rgba(88,151,251,0.71)" class="vs__spinner" />
                </template>
              </v-select>
            </div>
          </template>
          <template v-slot:cell(action)="data">
            <b-dropdown
              boundary="window"
              right
              variant="link"
              toggle-class="text-decoration-none"
              no-caret>
              <template v-slot:button-content>
                <v-icon name="ellipsis-v" />
              </template>
              <!-- <b-dropdown-item
                @click="onToggleDetails(data)">
                {{ data.detailsShowing ? 'Hide Details' : 'View Details' }}
              </b-dropdown-item> -->
              <b-dropdown-item
                v-if="isAccessible($options.CreditMemoPermissions.EDIT.id)"
                @click="setUpdate(data.item)"
                :disabled="showModalEntry">
                Edit
              </b-dropdown-item>
              <b-dropdown-item
                v-if="isAccessible($options.CreditMemoPermissions.DELETE.id)"
                @click="setDelete(data.item.id)"
                :disabled="showModalConfirmation">
                Delete
              </b-dropdown-item>
            </b-dropdown>
          </template>
          <template v-slot:head(client) >
            <CustomToolTip id="client-icon" label="CLIENT">
              <template #body>
              <div>
                <div>Line 1 - Represents the client name.</div>
                <div class="row__sub-label">Line 2 - Represents the client code.</div>
              </div>
              </template>
            </CustomToolTip>
          </template>
          <template v-slot:head(contract) >
            <CustomToolTip id="contract-icon" label="CONTRACT">
              <template #body>
                <div>
                  <div>Line 1 - Represents the tradename.</div>
                  <div class="row__sub-label">Line 2 - Represents the contract code.</div>
                </div>
              </template>
            </CustomToolTip>
          </template>

          <template v-slot:cell(client)="data">
            <div>{{ data.item.client.name }}</div>
            <div class="row__sub-label">{{ data.item.client.code }}</div>
          </template>
          <template v-slot:cell(contract)="data">
            <div>{{ data.item.contract.tradeName }}</div>
            <div class="row__sub-label">{{ data.item.contract.contractNo }}</div>
          </template>

          <template v-slot:cell(period)="data">
            <div>{{ `${$options.Months.getName(data.item.monthId)} ${data.item.year}` }}</div>
          </template>

          <!-- <template v-slot:cell(creditMemoNo)="data">
            <b-link @click="onToggleDetails(data)">{{ data.item.creditMemoNo }}</b-link>
          </template> -->

          <template #row-details="data">
            <BillingRowDetail :creditMemo="data.item" :loading="rowLoading" />
          </template>
        </Full>
      </template>
    </CustomCard>

    <CreditMemoEntry
      v-if="showModalEntry"
      :show="showModalEntry"
      @onBack="showModalEntry=false"
      @save="onCreditMemoEntry()"
      :mode="entryMode"
      :form="forms.creditMemo"/>

    <DeleteConfirmation
      @close="showModalConfirmation = false"
      @yes="onCreditMemoDelete()"
      :show="showModalConfirmation"
      :isProcessing="forms.creditMemo.isProcessing"
      name="Credit Memo" />
  </div>
</template>

<script>
import { CustomToolTip, CustomCard, CustomModal, DeleteConfirmation, SettingModal, AuthorizationModal } from "../../components";
import CreditMemoEntry from './CreditMemoEntry'
import { CreditMemoApi, BillingPeriodApi, ReportApi } from '../../../mixins/api';
import { Months, BillingStatuses, CreditMemoPermissions } from "../../../helpers/enum";
import Full from "../../components/Full";
import { clearFields, formatNumber, reset, showNotification, validate } from '../../../helpers/forms';
import { copyValue } from '../../../helpers/extractor';
import BillingRowDetail from '../../components/BillingRowDetail'
import Access from '../../../mixins/utils/Access'
import Settings from '../../../mixins/utils/Settings';

const creditMemoFields = {
  id: null,
  creditMemoNo: null,
  clientId: null,
  contractId: null,
  creditMemoDate: null,
  year: null,
  monthId: null,
  charges: null
}

export default {
  components: {
    Full,
    CreditMemoEntry,
    CustomToolTip,
    CustomCard,
    DeleteConfirmation,
    BillingRowDetail,
    CustomModal,
    AuthorizationModal,
    SettingModal
  },
  mixins: [ CreditMemoApi, ReportApi, BillingPeriodApi, Access, Settings ],
  BillingStatuses,
  Months,
  CreditMemoPermissions,
  data() {
    return {
      rowLoading: false,
      showModalEntry: false,
      showModalBatchEntry: false,
      showSettingModal: false,
      entryMode: 'Add',
      showModalConfirmation: false,
      showModalAuthorization: false,
      isProcessing: false,
      selectedBillingPeriod: [],
      forms: {
        creditMemo: {
          isProcessing: false,
          fields: { ...creditMemoFields },
          errors: { ...creditMemoFields },
          states: { ...creditMemoFields }      
        },
      },
      tables: {
        creditMemos: {
          isBusy: false,
          fields: [
            {
              key: 'creditMemoNo',
              label: 'Credit Memo No',
              thClass: 'align-middle',
              thStyle: { minWidth: '150px' },
              sortable: true
            },
            {
              key: 'client',
              label: 'CLIENT',
              thClass: 'align-middle',
              thStyle: { width: '18%' },
            },
            {
              key: 'contract',
              label: 'CONTRACT',
              thClass: 'align-middle',
              thStyle: { width: '18%' },
            },
            {
              key: 'creditMemoDate',
              label: 'Date',
              thClass: 'align-middle',
              thStyle: { width: '12%' },
            },
            {
              key: 'period',
              label: 'PERIOD',
              thClass: 'align-middle',
              thStyle: { width: '13%' },
            },
            {
              key: 'amount',
              label: 'AMOUNT',
              thClass: 'align-middle text-right',
              tdClass: 'text-right',
              thStyle: { width: '13%' },
              formatter: (value) => {
                return formatNumber(value)
              }
            },
            {
              key: 'action',
              label: '',
              thStyle: { width: '25px' },
              thClass: 'align-middle',
            },
          ],
          items: []
        }
      },
      paginations: {
        creditMemo: {
          from: 0,
          to: 0,
          totalRows: 0,
          page: 1,
          perPage: 10,
        }
      },
      filters: {
        creditMemo: {
          criteria: null,
          monthId: new Date().getMonth() + 1,
          year: new Date().getFullYear(),
          sortBy: 'credit_memo_no',
          sortDesc: true
        }
      },
      options: {
        billingPeriods: {
          isBusy: false,
          items: []
        }
      }
    }
  },
  created() {
    // this.loadCreditMemos()
    this.loadBillingPeriods()
  },
  methods: {
     sortChanged(event) {
      const { creditMemo } = this.filters
      creditMemo.sortBy =  event.sortBy
      creditMemo.sortDesc = event.sortDesc
      this.loadCreditMemos()
    },
    loadCreditMemos() {
      const { creditMemos } = this.tables
      const { creditMemo, creditMemo: { perPage, page } } = this.paginations
      const { monthId, year, criteria, sortBy, sortDesc } = this.filters.creditMemo
      const isDesc = sortDesc ? 'DESC' : 'ASC'
      const sortKey = sortBy === 'creditMemoNo' ? 'credit_memo_no' : 'id';

      const params = { paginate: true, perPage, page, monthId, year, criteria, filterByUser: true,  sortKey, sortDesc: isDesc }
      creditMemos.isBusy = true
      this.getCreditMemoList(params).then(( { data } ) => {
        creditMemos.items = data.data
        creditMemo.from = data.meta.from
        creditMemo.to = data.meta.to
        creditMemo.totalRows = data.meta.total
        creditMemos.isBusy = false
      })
    },
    loadBillingPeriods() {
      const { billingPeriods } = this.options
      const { creditMemo } = this.filters
      const params = { paginate: false }
      billingPeriods.isBusy = true
      this.getBillingPeriodList(params)
      .then(({ data }) => {
        billingPeriods.items = data
        const activeBillingPeriod = data.find(d => d.isActive)
        creditMemo.year = activeBillingPeriod.year
        creditMemo.monthId = activeBillingPeriod.monthId
        this.selectedBillingPeriod = activeBillingPeriod

        this.loadCreditMemos()
        billingPeriods.isBusy = false
      })
    },
    setCreate() {
      const { creditMemo, creditMemo: { fields } } = this.forms
      clearFields(creditMemo)
      reset(creditMemo)
      fields.charges = []
      this.entryMode = "Add"
      this.showModalEntry = true
    },
    setUpdate(row) {
      const { creditMemo, creditMemo: { fields } } = this.forms
      clearFields(creditMemo)
      reset(creditMemo)
      fields.charges = []
      this.getCreditMemo(row.id).then(( { data }) => {
        copyValue(data, fields)
        this.entryMode = "Edit"
        this.showModalEntry = true
      })
    },
    setDelete(id) {
      const { fields } = this.forms.creditMemo
      fields.id = id
      this.showModalConfirmation = true
    },
    onCreditMemoEntry() {
      const {
        creditMemo,
        creditMemo: {
          fields: {
            creditMemoNo,
            client,
            charges,
            ...fields
          }
        }
      } = this.forms
      creditMemo.isProcessing = true
      const chargeData = charges.map(c => {
        return {
          chargeId: c.id,
          amount: c.pivot.amount,
          notes: c.pivot.notes
        }
      })
      const data = {
        ...fields,
        charges: chargeData
      }
      reset(creditMemo)
      if (this.entryMode === 'Add') {
        this.addCreditMemo(data).then(({ data }) => {
          this.showModalEntry = false
          showNotification(this, "success", "Credit memo created successfully.")
          creditMemo.isProcessing = false
          this.loadCreditMemos()
        })
        .catch(error => {
          const errors = error.response.data.errors
          creditMemo.isProcessing = false
          validate(creditMemo, errors)
        })
      } else {
        this.updateCreditMemo(data, fields.id).then(({ data }) => {
          creditMemo.isProcessing = false
          this.showModalEntry = false
          showNotification(this, "success", "Credit memo updated successfully.")
          this.loadCreditMemos()
        })
        .catch(error => {
          const errors = error.response.data.errors
          creditMemo.isProcessing = false
          validate(creditMemo, errors)
        })
      }
    },
    onCreditMemoDelete() {
      const { creditMemo, creditMemo: { fields: { id } } } = this.forms
      creditMemo.isProcessing = true
      this.deleteCreditMemo(id)
        .then(({ data }) => {
          this.loadCreditMemos()
          creditMemo.isProcessing = false
          showNotification(this, "success", "Credit memo deleted successfully.")
          this.showModalConfirmation = false
        })
        .catch(error => {
          const errors = error.response.data.errors
          creditMemo.isProcessing = false
          if(errors.msg) {
            showNotification(this, "danger", errors.msg)
          }
        })
    },
    // onToggleDetails(row) {
    //   const creditMemoId = row.item.id
    //   if(!row.detailsShowing) {
    //     this.rowLoading = true
    //     this.getCreditMemo(creditMemoId).then(({ data }) => {
    //       copyValue(data, row.item)
    //       this.$set(row.item, 'charges', data.charges)
    //       // this.$set(row.item, 'bank', data.bank)
    //       this.rowLoading = false 
    //     })
    //   }
    //   row.toggleDetails()
    // },
    filterBillingPeriod(option, label, search) {
      let temp = search.toLowerCase();
      return option.month.name.toLowerCase().indexOf(temp) > -1 || 
      String(option.year).toLowerCase().indexOf(temp) > -1
    },
    onChangeBillingPeriod(billingPeriod) {
      if (billingPeriod) {
        console.log(billingPeriod)
        const { creditMemo } = this.filters
        creditMemo.year = billingPeriod.year
        creditMemo.monthId = billingPeriod.monthId
        this.loadCreditMemos()
      }
    },
  },
  computed: {
    defaultMonth() {
      return new Date().getMonth() + 1
    },
    defaultYear() {
      return new Date().getFullYear()
    }
  }
}
</script>

<style lang="scss" scoped>
  .month-filter {
    display: flex;
    align-items: center;
  }

  .row__sub-label {
    font-size: 8pt!important;
    color: gray!important;
  }

  .credit-memo__filter {
    display: flex;
    width: 100%;
    justify-content: flex-end;

    .billing-period__select {
      width: 180px;
    }
  }

</style>
